import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, RotateCcw, Loader2, AlertTriangle } from "lucide-react";
import { usePremium } from "../context/PremiumContext";
import { iapAvailable, getIapPackages, buyIapPackage, restoreIap } from "../lib/iap";
import { track } from "../lib/analytics";

const LABELS = { MONTHLY: "Monthly", ANNUAL: "Family · Yearly", LIFETIME: "Lifetime" };

// iOS-only paywall: Apple In-App Purchase via RevenueCat. No Stripe in the iOS app.
export const IapPlans = () => {
  const { deviceId, refreshPremium } = usePremium();
  const [pkgs, setPkgs] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!iapAvailable) { setPkgs([]); return; }
    getIapPackages().then(setPkgs).catch(() => { setPkgs([]); setMsg("Couldn't load App Store prices. Check your connection."); });
  }, []);

  const run = async (key, fn) => {
    setBusy(key); setMsg(null);
    try { await fn(); await refreshPremium(deviceId); }
    catch { setMsg("Purchase didn't go through. You haven't been charged."); }
    finally { setBusy(null); }
  };

  const buy = (p) => run(p.identifier, async () => {
    track("iap_buy_click", { package: p.packageType });
    if (await buyIapPackage(p)) track("iap_purchase_succeeded", { package: p.packageType });
  });

  if (pkgs === null) return <Loader2 size={20} className="animate-spin text-foreground/50" data-testid="iap-loading" />;

  return (
    <section data-testid="iap-plans" className="space-y-3">
      {pkgs.map((p) => (
        <button key={p.identifier} type="button" disabled={!!busy} onClick={() => buy(p)} data-testid={`iap-buy-${p.packageType.toLowerCase()}`}
          className="w-full text-left p-5 border-2 border-white/30 hover:border-brand-primary transition-colors space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-display font-black uppercase text-sm">{LABELS[p.packageType] || p.product.title}</span>
            {busy === p.identifier ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} className="text-brand-primary" />}
          </div>
          <div className="font-display font-black text-2xl">{p.product.priceString}</div>
          <div className="font-mono text-xs text-foreground/60">{p.product.description}</div>
        </button>
      ))}
      {pkgs.length === 0 && !msg && <p className="font-mono text-xs text-foreground/60">Purchases aren't available right now.</p>}
      <button type="button" disabled={!!busy} onClick={() => run("restore", restoreIap)} data-testid="iap-restore" className="btn-arcade btn-ghost w-full">
        {busy === "restore" ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />} Restore Purchases
      </button>
      {msg && <div className="border-2 border-brand-danger p-3 flex items-start gap-2 text-brand-danger font-mono text-xs" data-testid="iap-error"><AlertTriangle size={14} className="mt-0.5" /> {msg}</div>}
      <p className="font-mono text-[10px] text-foreground/50 leading-relaxed" data-testid="iap-legal">
        Payment is charged to your Apple ID at confirmation. Subscriptions renew automatically unless cancelled at least 24 hours before the end of the period, in your App Store account settings. Family Sharing is supported on the yearly plan.{" "}
        <Link to="/terms" className="underline">Terms of Use</Link> · <Link to="/privacy" className="underline">Privacy Policy</Link>
      </p>
    </section>
  );
};
